/**
 * Hàng chờ duyệt (Doc/11a "Luồng kiểm duyệt", E5/E6) — một hàng chờ cho cả phiếu chờ duyệt
 * lẫn đề xuất sửa dữ liệu chủ, xếp theo mức ảnh hưởng. Hai cột: danh sách trái, chi tiết phải;
 * J/K chuyển việc, A duyệt, R từ chối (prototype `design/prototype`, vai Điều phối viên).
 *
 * Đề xuất duyệt ngay tại đây: một quyết định cho cả gói (Q7), từ chối bắt buộc ghi chú, không
 * duyệt đề xuất của chính mình (Q5 — máy chủ chặn, nút ở đây chỉ nói trước). Phiếu thì mở màn
 * phiếu hiện có để duyệt — nghiệp vụ duyệt phiếu giữ nguyên.
 *
 * Phạm vi (đơn vị + địa bàn) do máy chủ áp cho cả hai danh sách (R8); ở đây không lọc thêm.
 *
 * Chưa có: cột "Giá trị nguồn" (cần `source_values`, phase 2) — bảng so sánh dùng giá trị
 * lúc gửi; lọc theo địa bàn và khoảng ngày.
 */
import * as api from '../api-client.js';
import { formLabel } from '@farmer/audit-rules';
import { toast, esc, formatDateTime, buildHash, parseHash } from '../ui/util.js';
import { ENTITY_LABEL, REASONS, displayValue, fieldLabel, shortId } from '../ui/proposal-fields.js';
import {
  FAR_FROM_RECORD_M,
  buildQueue,
  sortQueue,
  filterQueue,
  waitedText,
  distanceM,
  distanceText,
  staleItems,
  currentText,
  pendingForSubmission,
} from '../ui/review-queue-logic.js';

const REASON_LABEL = Object.fromEntries(REASONS);
const ACTION_LABEL = { update: 'Sửa', deactivate: 'Ngừng hoạt động' };

/** Tên bản ghi đích của đề xuất. Danh sách đề xuất không kèm tên, nên nạp từng bản ghi (có cache). */
async function loadRecords(crs) {
  const out = new Map();
  const getter = { household: api.households.get, plot: api.plots.get, middleman: api.middlemen.get };
  const uniq = new Map(crs.map((cr) => [cr.entityId, cr.entity]));
  await Promise.all(
    [...uniq].map(async ([id, entity]) => {
      try {
        out.set(id, await getter[entity](id));
      } catch {
        out.set(id, null); // đã xoá mềm hoặc ra khỏi phạm vi — vẫn hiện đề xuất, chỉ thiếu tên
      }
    }),
  );
  return out;
}

function recordTitle(entity, rec) {
  if (!rec) return 'Bản ghi không còn trong danh mục';
  if (entity === 'plot') return `Vườn ${rec.plotCode || ''} · hộ ${rec.household?.fullName || ''}`.trim();
  if (entity === 'middleman') return `${rec.name} (${rec.code})`;
  return rec.fullName;
}

function recordLink(entity, rec, entityId) {
  if (entity === 'plot') return rec?.householdId ? buildHash(`/households/${rec.householdId}`) : null;
  if (entity === 'middleman') return buildHash(`/middlemen/${entityId}`);
  return buildHash(`/households/${entityId}`);
}

function subjectOfSub(sub) {
  if (sub.middlemanId) return `${sub.middlemanName || ''} (${sub.middlemanCode || ''})`;
  return `${sub.householdName || ''}${sub.plotCode ? ' · ' + sub.plotCode : ''}`;
}

export async function renderReviewQueue(container) {
  const me = api.getCurrentUser();
  const { params } = parseHash();
  let selectedKey = params.id || null;
  const filters = { type: 'all', entity: '', actorId: '' };

  container.innerHTML = `
    <div class="page-head">
      <div><h1>Hàng chờ duyệt</h1><p>Phiếu chờ duyệt và đề xuất sửa dữ liệu chủ, xếp theo mức ảnh hưởng · J/K chuyển việc, A duyệt, R từ chối.</p></div>
    </div>
    <div class="filters-bar review-filters">
      <div class="seg" role="group" aria-label="Loại việc">
        <button type="button" data-type="all" class="is-on">Tất cả <span class="count" data-count="all"></span></button>
        <button type="button" data-type="cr">Đề xuất sửa <span class="count" data-count="cr"></span></button>
        <button type="button" data-type="sub">Phiếu <span class="count" data-count="sub"></span></button>
      </div>
      <select id="fEntity" aria-label="Đối tượng">
        <option value="">Mọi đối tượng</option>
        <option value="household">Hộ</option><option value="plot">Vườn</option><option value="middleman">Đại lý/MM</option>
      </select>
      <select id="fActor" aria-label="Điều tra viên"><option value="">Mọi điều tra viên</option></select>
    </div>
    <div class="review-split">
      <div class="review-list" id="reviewList" role="listbox" aria-label="Việc chờ duyệt"><p class="hint-text">Đang tải...</p></div>
      <section class="review-detail card" id="reviewDetail" aria-live="polite"></section>
    </div>`;

  const listHost = container.querySelector('#reviewList');
  const detailHost = container.querySelector('#reviewDetail');

  let crs = [];
  let records = new Map();
  let all = [];
  let shown = [];

  async function load() {
    try {
      const [crRes, submitted, flagged] = await Promise.all([
        api.changeRequests.list({ status: 'pending', pageSize: 200 }),
        api.submissions.list({ status: 'submitted', pageSize: 100 }),
        api.submissions.list({ needsReview: true, pageSize: 100 }),
      ]);
      crs = crRes.data || [];
      records = await loadRecords(crs);
      all = sortQueue(buildQueue(crs, [...(flagged.data || []), ...(submitted.data || [])]));
    } catch (err) {
      listHost.innerHTML = `<div class="empty-state">${esc(err.message)}</div>`;
      return;
    }
    if (!container.isConnected) return;
    fillActorFilter();
    renderList();
  }

  function fillActorFilter() {
    const sel = container.querySelector('#fActor');
    const names = new Map(all.map((it) => [it.actorId, it.actorName]));
    sel.innerHTML =
      '<option value="">Mọi điều tra viên</option>' +
      [...names].map(([id, n]) => `<option value="${esc(id)}" ${filters.actorId === id ? 'selected' : ''}>${esc(n || id)}</option>`).join('');
  }

  function itemHtml(it) {
    const on = it.key === selectedKey ? ' is-on' : '';
    if (it.type === 'cr') {
      const cr = it.cr;
      const n = cr.items?.length || 0;
      return `<button type="button" class="qrow${on}" role="option" aria-selected="${!!on}" data-key="${esc(it.key)}">
        <span class="qrow-head"><b>${esc(ACTION_LABEL[cr.action] || cr.action)} ${esc(ENTITY_LABEL[cr.entity].toLowerCase())}</b><span class="mono hint-text">${esc(shortId(cr.id))}</span></span>
        <span>${esc(recordTitle(cr.entity, records.get(cr.entityId)))}</span>
        <span class="hint-text">${cr.action === 'update' ? `${n} trường · ` : ''}${esc(it.actorName)} · chờ ${esc(waitedText(it.since))}</span>
        ${it.auditTouch ? '<span class="pending-chip">Đổi số dùng tính chỉ số</span>' : ''}
      </button>`;
    }
    const s = it.sub;
    return `<button type="button" class="qrow${on}${it.flagged ? ' is-flagged' : ''}" role="option" aria-selected="${!!on}" data-key="${esc(it.key)}">
      <span class="qrow-head"><b>Phiếu · ${esc(formLabel(s.formKey, 'short'))}</b>${it.flagged ? '<span class="badge status-rejected">Cần xem lại</span>' : ''}</span>
      <span>${esc(subjectOfSub(s))}</span>
      <span class="hint-text">${esc(it.actorName)} · chờ ${esc(waitedText(it.since))}${it.errorCount ? ` · <span class="is-errtxt">${it.errorCount} lỗi đối chiếu</span>` : ''}</span>
    </button>`;
  }

  function renderList() {
    shown = filterQueue(all, filters);
    for (const t of ['all', 'cr', 'sub']) {
      container.querySelector(`[data-count="${t}"]`).textContent = filterQueue(all, { ...filters, type: t }).length;
    }
    if (!shown.length) {
      listHost.innerHTML = `<div class="empty-state"><span class="ms ms-xl">task_alt</span><p>Không còn việc nào chờ duyệt.</p></div>`;
      detailHost.innerHTML = '';
      return;
    }
    if (!shown.some((it) => it.key === selectedKey)) selectedKey = shown[0].key;
    listHost.innerHTML = shown.map(itemHtml).join('');
    listHost.querySelectorAll('.qrow').forEach((b) => b.addEventListener('click', () => select(b.dataset.key)));
    renderDetail();
  }

  function select(key) {
    selectedKey = key;
    listHost.querySelectorAll('.qrow').forEach((b) => {
      const on = b.dataset.key === key;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-selected', String(on));
      if (on) b.scrollIntoView({ block: 'nearest' });
    });
    renderDetail();
  }

  let detailToken = 0;
  async function renderDetail() {
    const it = shown.find((x) => x.key === selectedKey);
    if (!it) return;
    // Giữ việc đang xem trong URL để tải lại trang không mất chỗ — không phát hashchange.
    history.replaceState(null, '', buildHash('/review', { id: it.key }));
    const token = ++detailToken;
    if (it.type === 'sub') return renderSubDetail(it);
    detailHost.innerHTML = '<p class="hint-text">Đang tải...</p>';
    let impact = null;
    try {
      impact = await api.changeRequests.impact(it.cr.id);
    } catch (err) {
      console.warn('[review] không tải được ảnh hưởng', err);
    }
    if (token !== detailToken) return; // người duyệt đã chuyển sang việc khác
    renderCrDetail(it, impact);
  }

  function renderSubDetail(it) {
    const s = it.sub;
    const pend = pendingForSubmission(s, crs);
    detailHost.innerHTML = `
      <div class="eyebrow">Phiếu · ${esc(formLabel(s.formKey))}</div>
      <h2>${esc(subjectOfSub(s))}</h2>
      <p class="hint-text">${esc(it.actorName)} · nộp ${esc(formatDateTime(s.submittedAt))}</p>
      ${it.flagged ? `<div class="status-banner tone-error"><span class="ms">flag</span><span><b>Cần xem lại:</b> ${esc(s.needsReviewReason)}</span></div>` : ''}
      ${pend.length ? `<div class="status-banner tone-warn"><span class="ms">pending_actions</span>
        <span>Chủ thể có <b>${pend.length} đề xuất chờ duyệt</b> — nên xử lý đề xuất trước khi duyệt phiếu.</span>
        <button type="button" class="btn btn-tonal sm" data-goto="cr:${esc(pend[0].id)}">Xem đề xuất</button></div>` : ''}
      <dl class="info-list">
        <div><dt>Trạng thái</dt><dd>${esc(s.status === 'submitted' ? 'Chờ duyệt' : s.status)}</dd></div>
        <div><dt>Lỗi đối chiếu (mức đỏ)</dt><dd>${it.errorCount || 'Không có'}</dd></div>
      </dl>
      <div class="review-actions">
        <a class="btn" href="${buildHash(`/submissions/${s.id}`)}"><span class="ms">open_in_new</span>Mở phiếu để duyệt</a>
      </div>`;
    detailHost.querySelector('[data-goto]')?.addEventListener('click', (e) => {
      filters.type = 'all';
      syncFilterUi();
      renderList();
      select(e.currentTarget.dataset.goto);
    });
  }

  function impactGroupHtml(title, rows, { checkable = false, hint = '' } = {}) {
    if (!rows?.length) return '';
    return `<div class="impact-group">
      <h4>${esc(title)} (${rows.length})</h4>
      ${hint ? `<p class="hint-text">${esc(hint)}</p>` : ''}
      <ul>${rows
        .map(
          (r) => `<li>${
            checkable ? `<label class="chk"><input type="checkbox" name="reopen" value="${esc(r.id)}">` : ''
          }<a href="${buildHash(`/submissions/${r.id}`)}">${esc(formLabel(r.formKey, 'short'))}</a>
          · ${esc(r.enumerator?.fullName || '')}${r.submittedAt ? ` · nộp ${esc(formatDateTime(r.submittedAt))}` : ''}${
            r.snapshotAreaHa ? ` · chốt ${esc(r.snapshotAreaHa)} ha` : ''
          }${checkable ? '</label>' : ''}</li>`,
        )
        .join('')}</ul></div>`;
  }

  function renderCrDetail(it, impact) {
    const cr = it.cr;
    const rec = records.get(cr.entityId);
    const stale = staleItems(cr, rec);
    const self = cr.requestedBy === me?.id;
    const link = recordLink(cr.entity, rec, cr.entityId);
    const dist = distanceM({ lat: cr.gpsLat, lng: cr.gpsLng }, { lat: rec?.gpsLat, lng: rec?.gpsLng });
    const where = cr.entity === 'plot' ? 'vườn' : cr.entity === 'household' ? 'nhà' : 'đại lý';

    const rows = (cr.items || [])
      .map((item) => {
        const old = displayValue(cr.entity, item.field, item.oldValue) ?? '—';
        const cur = displayValue(cr.entity, item.field, currentText(rec, item.field)) ?? '—';
        const neu = displayValue(cr.entity, item.field, item.newValue) ?? '(xoá trống)';
        const isStale = stale.includes(item);
        return `<tr>
          <th scope="row">${esc(fieldLabel(cr.entity, item.field))}</th>
          <td>${esc(old)}</td>
          <td class="${isStale ? 'is-errtxt' : ''}">${esc(cur)}${isStale ? ' ⚠' : ''}</td>
          <td class="diff-new">${esc(neu)}</td>
          <td>${esc(REASON_LABEL[item.reasonCode] || item.reasonCode)}${item.reasonNote ? `: ${esc(item.reasonNote)}` : ''}</td>
        </tr>`;
      })
      .join('');

    detailHost.innerHTML = `
      <div class="eyebrow">${esc(shortId(cr.id))} · ${esc(ACTION_LABEL[cr.action] || cr.action)} ${esc(ENTITY_LABEL[cr.entity].toLowerCase())}</div>
      <h2>${esc(recordTitle(cr.entity, rec))}</h2>
      <p class="hint-text">Gửi bởi <b>${esc(it.actorName)}</b> · ${esc(formatDateTime(cr.requestedAt))} · đã chờ ${esc(waitedText(cr.requestedAt))}
        ${link ? ` · <a href="${link}">Mở hồ sơ</a>` : ''}</p>

      ${stale.length ? `<div class="status-banner tone-warn"><span class="ms">history</span>
        <span>Giá trị hiện hành của <b>${stale.map((s) => esc(fieldLabel(cr.entity, s.field))).join(', ')}</b> đã đổi kể từ lúc gửi.
        Duyệt sẽ ghi đè giá trị đó — hãy chắc trước khi duyệt.</span></div>` : ''}

      ${cr.action === 'deactivate'
        ? `<div class="status-banner tone-warn"><span class="ms">block</span><span>Đề xuất <b>ngừng hoạt động</b> bản ghi này. Duyệt là ẩn khỏi tìm kiếm và không tạo phiếu mới được nữa.</span></div>`
        : `<div class="card-scroll"><table class="list-table compare-table">
            <thead><tr><th>Trường</th><th>Lúc gửi</th><th>Hiện hành</th><th>Đề xuất</th><th>Lý do</th></tr></thead>
            <tbody>${rows}</tbody></table></div>`}

      <div class="evidence">
        <div><span class="hint-text">Ghi chú của điều tra viên</span><div>${cr.note ? esc(cr.note) : '—'}</div></div>
        <div><span class="hint-text">Vị trí lúc gửi</span><div>${
          cr.gpsLat == null
            ? 'Không có vị trí'
            : dist === null
              ? `${esc(cr.gpsLat)}, ${esc(cr.gpsLng)} (bản ghi chưa có toạ độ để so)`
              : `${dist > FAR_FROM_RECORD_M ? '<span class="is-errtxt">⚠ ' : ''}Gửi cách ${where} ${esc(distanceText(dist))}${dist > FAR_FROM_RECORD_M ? '</span>' : ''}`
        }${cr.gpsAccuracyM ? ` <span class="hint-text">(±${Math.round(cr.gpsAccuracyM)} m)</span>` : ''}</div></div>
      </div>

      <h3 class="card-title"><span class="ms">account_tree</span>Ảnh hưởng tới phiếu</h3>
      ${
        !impact
          ? '<p class="hint-text">Không tải được danh sách phiếu bị ảnh hưởng.</p>'
          : !impact.drafts.length && !impact.usingProposal.length && !impact.submittedBefore.length
            ? '<p class="hint-text">Chưa có phiếu nào tham chiếu bản ghi này.</p>'
            : `${impact.areaFactor ? `<p class="hint-text">Chỉ số /ha của phiếu tính theo diện tích cũ sẽ nhân <b>×${String(impact.areaFactor).replace('.', ',')}</b> nếu tính lại.</p>` : ''}
               ${
                 // Chỉ diện tích / loại đất được chiếu vào phiếu nháp (Q1); sửa SĐT, tên... thì
                 // phiếu nháp chỉ tham chiếu bản ghi, không tính theo gì cả.
                 impact.auditFields.length
                   ? impactGroupHtml('Phiếu nháp đang tính theo giá trị đề xuất', impact.drafts, { hint: 'Không cần làm gì — duyệt thì giữ, từ chối thì phiếu nháp tự quay về giá trị hồ sơ.' })
                   : impactGroupHtml('Phiếu nháp tham chiếu bản ghi này', impact.drafts, { hint: 'Đề xuất không chạm số dùng tính chỉ số — phiếu không đổi kết quả đối chiếu.' })
               }
               ${impactGroupHtml('Phiếu đã nộp chốt theo giá trị đề xuất', impact.usingProposal, { hint: 'Từ chối → các phiếu này được gắn cờ "Cần xem lại" và lên đầu hàng chờ.' })}
               ${impactGroupHtml('Phiếu nộp trước khi có đề xuất', impact.submittedBefore, { checkable: true, hint: 'Đánh dấu phiếu cần mở lại để điều tra viên nộp lại theo giá trị mới (chỉ khi duyệt).' })}`
      }

      <div class="decide">
        <div class="form-row">
          <label for="decisionNote">Ghi chú của người duyệt <span class="hint-text">(bắt buộc khi từ chối — điều tra viên sẽ đọc)</span></label>
          <textarea id="decisionNote" rows="2"></textarea>
        </div>
        ${self ? '<p class="hint-text"><span class="ms ms-sm">block</span> Đây là đề xuất của bạn — người khác phải duyệt.</p>' : ''}
        <div class="error-text" id="decideError" role="alert" hidden></div>
        <div class="review-actions">
          <button type="button" class="btn" data-decide="approved" ${self ? 'disabled' : ''}><span class="ms">check</span>Duyệt toàn bộ</button>
          <button type="button" class="btn btn-text danger" data-decide="rejected" ${self ? 'disabled' : ''}><span class="ms">close</span>Từ chối</button>
        </div>
      </div>`;

    detailHost.querySelectorAll('[data-decide]').forEach((b) =>
      b.addEventListener('click', () => decide(it, b.dataset.decide, false)),
    );
  }

  async function decide(it, decision, acknowledgeStale) {
    const noteEl = detailHost.querySelector('#decisionNote');
    const errEl = detailHost.querySelector('#decideError');
    const note = noteEl.value.trim();
    errEl.hidden = true;
    if (decision === 'rejected' && !note) {
      errEl.textContent = 'Ghi chú lý do từ chối để điều tra viên biết cần làm gì.';
      errEl.hidden = false;
      noteEl.focus();
      return;
    }
    const reopenSubmissionIds =
      decision === 'approved' ? [...detailHost.querySelectorAll('input[name="reopen"]:checked')].map((c) => c.value) : [];
    detailHost.querySelectorAll('[data-decide]').forEach((b) => (b.disabled = true));
    try {
      const res = await api.changeRequests.decide(it.cr.id, {
        decision,
        ...(note ? { note } : {}),
        ...(reopenSubmissionIds.length ? { reopenSubmissionIds } : {}),
        ...(acknowledgeStale ? { acknowledgeStale: true } : {}),
      });
      const extra = [
        res.reopenedSubmissionIds?.length ? `mở lại ${res.reopenedSubmissionIds.length} phiếu` : '',
        res.flaggedSubmissionIds?.length ? `gắn cờ ${res.flaggedSubmissionIds.length} phiếu` : '',
      ].filter(Boolean);
      toast(`${decision === 'approved' ? 'Đã duyệt' : 'Đã từ chối'} đề xuất ${shortId(it.cr.id)}${extra.length ? ' · ' + extra.join(', ') : ''}.`);
      // Từ chối có thể vừa gắn cờ phiếu — tải lại để chúng lên đầu hàng chờ.
      const idx = shown.findIndex((x) => x.key === it.key);
      selectedKey = shown[idx + 1]?.key || shown[idx - 1]?.key || null;
      await load();
    } catch (err) {
      if (err.code === 'STALE_CHANGE_REQUEST') {
        const fields = (err.body?.staleFields || []).map((f) => fieldLabel(it.cr.entity, f.field)).join(', ');
        errEl.innerHTML = `${esc(`Giá trị hiện hành của ${fields || 'một số trường'} đã đổi kể từ lúc gửi.`)}
          <button type="button" class="btn btn-tonal sm" data-ack>Vẫn duyệt</button>`;
        errEl.querySelector('[data-ack]').addEventListener('click', () => decide(it, decision, true));
      } else {
        errEl.textContent = err.message;
      }
      errEl.hidden = false;
      detailHost.querySelectorAll('[data-decide]').forEach((b) => (b.disabled = it.cr.requestedBy === me?.id));
    }
  }

  function syncFilterUi() {
    container.querySelectorAll('[data-type]').forEach((b) => b.classList.toggle('is-on', b.dataset.type === filters.type));
  }

  container.querySelectorAll('[data-type]').forEach((b) =>
    b.addEventListener('click', () => {
      filters.type = b.dataset.type;
      syncFilterUi();
      renderList();
    }),
  );
  container.querySelector('#fEntity').addEventListener('change', (e) => {
    filters.entity = e.target.value;
    renderList();
  });
  container.querySelector('#fActor').addEventListener('change', (e) => {
    filters.actorId = e.target.value;
    renderList();
  });

  // Bàn phím: chỉ khi không gõ trong ô nhập, để chữ "a"/"r" trong ghi chú không thành lệnh.
  const onKey = (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.target.closest?.('input, textarea, select, [contenteditable]')) return;
    const k = e.key.toLowerCase();
    const i = shown.findIndex((x) => x.key === selectedKey);
    if (k === 'j' && shown[i + 1]) select(shown[i + 1].key);
    else if (k === 'k' && shown[i - 1]) select(shown[i - 1].key);
    else if (k === 'a') detailHost.querySelector('[data-decide="approved"]:not(:disabled)')?.click();
    else if (k === 'r') {
      const note = detailHost.querySelector('#decisionNote');
      if (note) {
        e.preventDefault();
        note.focus(); // từ chối cần ghi chú trước — R đưa con trỏ vào đó
      }
    } else return;
  };
  document.addEventListener('keydown', onKey);

  await load();

  return () => document.removeEventListener('keydown', onKey);
}
